import AMPLITUDE from '@constants/amplitude'
import GTM_CONSTANTS from '@constants/google-tag-manager'
import ENV from '@src/env'
import { i18nTranslate } from '@src/utils/i18n-translate'
import { arrayUniqueByKey, onlyUnique } from '@state/helpers'
import axios from 'axios'
import 'firebase/database'
import get from 'lodash/get'
import Raven from 'raven-js'
import Vue from 'vue'
import router from '../../router'

const i18n = i18nTranslate(Vue)

const getDefaultState = () => {
  return {
    common: {
      solutionType: {},
      products: null,
      createdBy: null,
      orderId: null,
      storeType: null,
      country: null,
      cityId: null,
      comment: null,
      upload: null,
      ticketQueryParams: {},
      isGuestArticle: false,
    },
    ticket: {
      custom_fields: [],
      comment: {
        body: null,
      },
    },
    uploads: [],
    files: [],
    createdTicket: null,
    fetching: false,
    requiredFields: [],
    validatedFields: [],
    ticketsByOrder: [],
  }
}

export const state = getDefaultState()

export const getters = {
  computedTicket(state, getters) {
    const {
      computedCustomFields,
      computedSubject,
      computedContactKey,
      formatedBody,
      computedTags,
      userData,
      zendeskUploadsTokens,
      widgetsCommon,
    } = getters

    return {
      articleId: widgetsCommon.params.articleId,
      custom_fields: computedCustomFields,
      submitter_id: null,
      requester_id: null,
      subject: computedSubject,
      comment: {
        body: formatedBody,
        uploads: zendeskUploadsTokens,
      },
      tags: computedTags,
      contact_key: computedContactKey,
      requester: {
        name: userData.name,
        email: userData.email,
      },
    }
  },
  isGuestArticle(state) {
    return state.common.isGuestArticle;
  },
  getTicketQueryParams(state) {
    return state.ticketQueryParams || new URLSearchParams()
  },
  baseCustomFields(state, getters, rootState, rootGetters) {
    const { SharedData } = rootState.widgets.data
    const { baseProducts, rappipayCustomFields } = getters
    const customFields = SharedData ? SharedData.customFields : []
    customFields.push({ key: 'replicate_ticket_to_provider', value: true })

    let mappedCF = customFields.map(cf => {
      if (cf.key === 'products')
        return Object.assign({}, cf, {
          value: JSON.stringify(baseProducts),
        })
      return cf
    })

    mappedCF = mappedCF.filter(({ value, key }) => {
      if (key === 'order_id') {
        return value.localeCompare(':order_id') !== 0
      }
      return true
    })

    return [...mappedCF, ...rappipayCustomFields]
  },
  computedCustomFields(state, getters) {
    const { baseCustomFields } = getters
    const { custom_fields: customFields } = state.ticket

    return arrayUniqueByKey(
      customFields.concat(baseCustomFields),
      'key'
    ).filter(cf => cf.value !== '')
  },
  rappipayCustomFields(state, getters) {
    const {
      widgetsCommon: {
        query: {
          dev,
          token,
          order_id: orderId,
          country_code: countryCode,
          language,
          ...rappipayFields
        },
      },
    } = getters // eslint-disable-line

    const fields = Object.entries(rappipayFields)

    return fields.map(([key, value]) => ({ key, value }))
  },
  userData(state, getters, rootState) {
    const { data, common } = rootState.widgets
    const info = data.SharedData ? data.SharedData.userData : {}
    const meta = common.user ? common.user : {}
    return Object.assign({}, info, meta)
  },
  customField(state, getters) {
    return key => getters.computedCustomFields.find(cf => cf.key === key)
  },
  baseProducts(state, getters, rootState) {
    const { SharedData } = rootState.widgets.data
    return SharedData && SharedData.orderData
      ? SharedData.orderData.products
      : []
  },
  computedProducts(state, getters) {
    const { baseProducts } = getters
    const { products } = state.common
    return products || baseProducts
  },
  productsCF(state, getters) {
    const products = getters.customField('products')
    return products || {}
  },
  flowToExecute(state, getters) {
    const solutionType = getters.customField('solution_type')
    const solutionTypeOld = getters.customField('tipoSolucion')

    const value =
      solutionType && solutionType.value !== ''
        ? solutionType.value
        : solutionTypeOld && solutionTypeOld.value !== ''
        ? solutionTypeOld.value
        : ''

    switch (value) {
      case 'reenvío_de_producto': {
        return 'REORDER_PRODUCTS'
      }
      case 'reembolso_en_rappicreditos': {
        return 'RC_REFUND'
      }
      case 'contact_support': {
        return 'CONTACT_SUPPORT'
      }
      default: {
        return 'CONTACT_SUPPORT'
      }
    }
  },
  widgetsCommon(state, getters, rootState) {
    const { common } = rootState.widgets
    return common || {}
  },
  widgetData(state, getters, rootState) {
    const { data } = rootState.widgets
    return (name) => data[name]
  },
  computedComment(state) {
    const { comment } = state.common
    return comment || {}
  },
  selectedCompensation(state) {
    const { solutionType } = state.common
    return solutionType || {}
  },
  computedSubject(state, getters, rootState) {
    const { ZendeskArticle } = rootState.widgets.data
    return ZendeskArticle ? ZendeskArticle.title || ZendeskArticle.name : ''
  },
  computedContactKey(state, getters, rootState) {
    const { ZendeskArticle } = rootState.widgets.data
    return ZendeskArticle ? ZendeskArticle.tag : null
  },
  computedTags(state, getters, rootState) {
    const { ZendeskArticle } = rootState.widgets.data
    return ZendeskArticle
      ? ZendeskArticle.label_names || ZendeskArticle.tags
      : []
  },
  computedOption(state) {
    const {
      doubt_type: doubtType, // eslint-disable-line
      solution_type: solutionType, // eslint-disable-line
    } = state.common

    return (
      {
        doubtType,
        solutionType,
      } || {}
    )
  },
  computedSolutionType(state, getters) {
    const { computedCustomFields } = getters

    const [solutionType] = computedCustomFields.filter(
      e =>
        e.key === 'solution_type' ||
        e.key === 'SolutionType' ||
        e.key === 'tipoSolucion'
    )

    return solutionType ? solutionType.value : null
  },
  computedRtPaymentMessage(state, getters, rootState, rootGetters) {
    const message = rootGetters['widgets/rtIssuedPaymentState']
      ? `${i18n.t('WIDGETS.PAYMENTS_RT.PUBLIC_COMMENT_RANGE', {
          range: rootGetters['widgets/rtIssuedPaymentState'].range,
        })}
      ${i18n.t('WIDGETS.PAYMENTS_RT.PUBLIC_COMMENT_AMOUNT', {
        amount: rootGetters['widgets/rtIssuedPaymentState'].amount,
      })}
      `
      : null

    return message
  },
  computedDoubtType(state, getters) {
    const { computedCustomFields } = getters

    const [doubtType] = computedCustomFields.filter(
      e =>
        e.key === 'doubt_type' || e.key === 'DoubtType' || e.key === 'tipoDuda'
    )
    return doubtType ? doubtType.value : null
  },
  publicNoteHead(state, getters) {
    return `
    AID: ${getters.widgetsCommon.params.articleId}
    OID: ${getters.widgetsCommon.query.order_id}
    `
  },
  formatedBody(state, getters) {
    const products = getters.productsCF.value
      ? JSON.parse(getters.productsCF.value)
      : []
    const solutionTypeText = `
      ${i18n.t('ARTICLES.QUESTIONS.HOW_SOLVE_YOUR_PROBLEM')}
      ${getters.computedSolutionType}`

    const doubtTypeText = `
      ${i18n.t('ARTICLES.QUESTIONS.KIND_OF_DOUBT')}
      ${getters.computedDoubtType}`

    const commentText = `
      ${i18n.t('ARTICLES.QUESTIONS.USER_COMMENT')}
      ${getters.computedComment.body}`

    return `
${products.length > 0 ? i18n.t('ARTICLES.QUESTIONS.PRODUCT_DESCRIPTION') : ''}
${getters.formatProductsComment}
${getters.computedSolutionType ? solutionTypeText : ''}
${getters.computedDoubtType ? doubtTypeText : ''}
${getters.computedComment.body ? commentText : ''}
${getters.computedRtPaymentMessage ? getters.computedRtPaymentMessage : ''}
`
  },
  formatProductsComment(state, getters, rootState) {
    const products = getters.productsCF.value
      ? JSON.parse(getters.productsCF.value)
      : []
    return products
      .map(p => {
        const unitPrice =
          p.paid_unit_price || p.unit_price || (p.pivot && p.pivot.unit_price)
        const complainedUnits =
          p.complainedUnits || p.units || (p.pivot && p.pivot.units)
        return `
  Product Name: ${p.name}
  Product Price: ${unitPrice * complainedUnits}
  Quantity: ${complainedUnits}
`
      })
      .toString()
      .replace(/,/g, '')
  },
  uploadAttachment(state, getters, rootState) {
    const { upload } = state.common
    return upload ? [upload.token] : []
  },
  zendeskUploadsTokens(state) {
    const { uploads } = state
    return uploads.map(u => u.resourceId)
  },
  validateSend(state, getters) {
    const { requiredFields, validatedFields } = state
    const uniques = onlyUnique(validatedFields, requiredFields, 'key')
    return uniques
  },
  commentValidation(state, getters) {
    return getters.computedComment.body
      ? getters.computedComment.body.length !== 0
      : false
  },
  attachmentValidation(state) {
    return state.files.length > 0
  },
  requiredCustomFieldsValidation(state, getters) {
    let isValid = true
    if (state.requiredFields.length) {
      for (const requiredField of state.requiredFields) {
        if (requiredField.type === 'function') {
          isValid = getters[requiredField.key]
        } else {
          const field = getters.computedCustomFields.find(
            ({ id, value }) =>
              id === requiredField.key && value && value.length !== 0
          )
          isValid = !!field
        }

        if (!isValid) {
          break
        }
      }
    }
    return isValid
  },
  productsValidation(state) {
    const products = state.ticket.custom_fields.find(p => p.key === 'products')

    return products ? JSON.parse(products.value).length : false
  },
}

export const mutations = {
  SET_TICKET_UPDATE(state, { key, data }) {
    state.ticket = Object.assign({}, state.ticket, {
      [key]: Object.assign({}, state.ticket[key], data),
    })
  },
  SET_COMMON_UPDATE(state, { key, data }) {
    state.common = Object.assign({}, state.common, {
      [key]: Object.assign({}, state.common[key], data),
    })
  },
  SET_REQUIRED_FIELDS(state, data) {
    const index = state.requiredFields.map(e => e.key).indexOf(data.key)

    if (index > -1) {
      state.requiredFields[index] = data
    } else {
      state.requiredFields = [...state.requiredFields, data]
    }

    if (data.evaluate && !data.isTrue) {
      state.requiredFields.splice(index, 1)
    }
  },
  SET_CUSTOM_FIELD(state, data) {
    const { custom_fields: customFields } = state.ticket

    const index = customFields.findIndex(cf => {
      if (data.key) return data.key === cf.key
      return data.id === cf.id
    })

    if (index === -1) {
      customFields.push(data)
    } else {
      Vue.set(customFields, index, data)
    }
  },
  TOGGLE_FETCH(state, value) {
    state.fetching = value
  },
  ADD_TICKET_QUERY(state, value) {
    state.ticketQueryParams = value
  },
  ADD_ZENDESK_ATTACHMENT(state, data) {
    state.uploads = [...state.uploads, data]
  },
  ADD_ZENDESK_ATTACHMENTS(state, data) {
    state.uploads = data
  },
  ADD_ATTACHMENT_FILE(state, data) {
    state.files = [...state.files, data]
  },
  REMOVE_ZENDESK_ATTACHMENT(state, token) {
    const { files } = state
    const index = files.findIndex(u => u.token === token)
    state.files = [
      ...files.slice(0, index),
      ...files.slice(index + 1, files.length),
    ]
  },
  ADD_VALID_FIELD(state, data) {
    const index = state.validatedFields.map(e => e.key).indexOf(data.key)

    if (index > -1) {
      Vue.set(state.validatedFields, index, data)
    } else {
      state.validatedFields = [...state.validatedFields, data]
    }
  },
  REMOVE_VALID_FIELD(state, data) {
    state.validatedFields = state.validatedFields.filter(
      field => data.key !== field.key
    )
  },
  RESET_STATE(state) {
    // Merge rather than replace so we don't lose observers
    Object.assign(state, getDefaultState())
  },
  SET_TICKETS_BY_ORDER(state, data) {
    if (data) state.ticketsByOrder = data
  },
  ADD_FIREBASE_URL(state, data) {
    state.firebaseURL = data;
  },
  ADD_FIREBASE_TOKEN(state, data) {
    state.firebaseToken = data
  },
  ADD_FIREBASE_TICKET_SCREEN(state, data) {
    state.firebaseTicketScreen = data
  },
  SET_GUEST_ARTICLE(state, data) {
    state.common.isGuestArticle = data
  }
}


export const actions = {
  async createTicket(
    { getters, rootGetters, dispatch, commit },
    settings = { params: {} }
  ) {
    const { computedTicket, userData } = getters

    const SO_ticketAutomationFlow = // eslint-disable-line
      rootGetters['countryData/getTicketAutomationFlow']
    const useTicketAutomation =
      SO_ticketAutomationFlow && SO_ticketAutomationFlow.value === true // eslint-disable-line
    const { additionalTags } = settings.params || []

    // log create ticket intention

    this.dataLayerPush({
      user: userData,
      event: GTM_CONSTANTS.LOG_EVENT,
      eventType: AMPLITUDE.CREATE_TICKET,
      payload: computedTicket,
    })

    if (settings.params.provider) {
      await commit('ADD_TICKET_QUERY', settings.params)
      await dispatch('automatedTicket')
      await dispatch('resetZendeskState')
    } else if (useTicketAutomation) {
      await dispatch('automatedTicket', additionalTags)
      await dispatch('resetZendeskState')
    } else {
      await dispatch('automatedTicketByFlowActions', additionalTags)
    }
  },
  async getTicketStatus({ getters, rootGetters }) {
    const { widgetsCommon } = getters
    const baseTim = `${
      ENV.rappi.url[rootGetters['widgets/userEnv']]
    }/api/ticket-manager/tickets/order/${widgetsCommon.query.order_id}`

    try {
      let { data } = await axios.get(baseTim, {
        headers: {
          Authorization: `Bearer ${widgetsCommon.query.token}`,
        },
      })
      if (data) {
        const channel = data.channel || ''
        const ticketId = data.ticketId || ''

        return { channel, ticketId };
      }
    } catch (e) {
      Raven.captureException(e)
    }
  },
  async getTicketsWithourOrderId({ getters, rootGetters, commit}) {
    const { widgetsCommon } = getters
    const baseTim = `${
      ENV.rappi.url[rootGetters['widgets/userEnv']]
    }/api/ticket-manager/tickets/user?page=1&status=open`

    try {
      let { data } = await axios.get(baseTim, {
        headers: {
          Authorization: `Bearer ${widgetsCommon.query.token}`,
        },
      })

      return data.docs.find(
        ({ orderId, articleId }) =>
          orderId === '' && articleId === widgetsCommon.params.articleId
      )
    } catch (err) {
      await commit('TOGGLE_FETCH', false)
      this.dataLayerPush({
        event: GTM_CONSTANTS.LOG_EVENT,
        eventType: AMPLITUDE.SERVER_ERROR,
        payload: err.response,
      })
      Raven.captureException(err)
    }
  },
  async createTicketForChat({ getters, rootGetters, commit, dispatch }) {
    const { computedTicket, widgetsCommon, userData } = getters
    try {
      const baseTim = `${
        ENV.rappi.url[rootGetters['widgets/userEnv']]
      }/api/ticket-manager/tickets/chat`


      const { data: attachments } = await dispatch('uploadFiles')

      await commit('ADD_ZENDESK_ATTACHMENTS', attachments)

      const ticket = Object.assign({}, computedTicket, {
        comment: {
          body: computedTicket.comment.body,
          uploads: getters.zendeskUploadsTokens,
        },
      })

      let { data } = await axios.post(baseTim, ticket, {
        headers: {
          Authorization: `Bearer ${widgetsCommon.query.token}`,
        },
        params: getters.getTicketQueryParams,
      });

      if (data) {
        this.dataLayerPush({
          user: userData,
          event: GTM_CONSTANTS.LOG_EVENT,
          eventType: AMPLITUDE.CREATED_CHAT,
          payload: data,
        })
      }

    } catch (err) {
      commit('TOGGLE_FETCH', false)
      router.push({
        name: 'screen',
        params: {
          screenId: 'ERROR_TICKET_CREATION',
        },
        query: widgetsCommon.query,
      })
      this.dataLayerPush({
        user: userData,
        event: GTM_CONSTANTS.LOG_EVENT,
        eventType: AMPLITUDE.SERVER_ERROR,
        payload: err.response,
      })
      Raven.captureException(err)
    }
  },
  async getFirebaseDatabases({ rootState, dispatch }) {
    await dispatch('firebaseData/initializeFirebase', null, { root: true })
  },
  async automatedTicket(
    { state, commit, getters, rootGetters, dispatch, rootState },
    additionalTags = null
  ) {
    const { computedTicket, widgetsCommon, userData, widgetData } = getters

    try {
      const baseTim = `${
        ENV.rappi.url[rootGetters['widgets/userEnv']]
      }/api/ticket-manager/tickets/?wait-automation=true`

      await commit('TOGGLE_FETCH', true)

      const { data: attachments } = await dispatch('uploadFiles')

      await commit('ADD_ZENDESK_ATTACHMENTS', attachments)

      if (additionalTags)
        computedTicket.tags = computedTicket.tags.concat(additionalTags)

      const ticket = Object.assign({}, computedTicket, {
        comment: {
          body: computedTicket.comment.body,
          uploads: getters.zendeskUploadsTokens,
        },
      })

      let { data } = await axios.post(baseTim, ticket, {
        headers: {
          Authorization: `Bearer ${widgetsCommon.query.token}`,
        },
        params: getters.getTicketQueryParams,
      })

      const widget = widgetData('Button')

      const type = get(widget, 'action.type', '')

      const appendTimId = get(widget, 'action.data.appendTimId', false)

      if (type === 'createTicket' && appendTimId) {
        await dispatch('widgets/commitQuery', { tim_id: data._id }, { root: true })
      }

      if (data) {
        this.dataLayerPush({
          user: userData,
          event: GTM_CONSTANTS.LOG_EVENT,
          eventType: AMPLITUDE.TICKET_CREATED,
          payload: data,
        })
      }

      await dispatch('firebaseData/initializeFirebase', null, { root: true })
    } catch (err) {
      commit('TOGGLE_FETCH', false)
      router.push({
        name: 'screen',
        params: {
          screenId: 'ERROR_TICKET_CREATION',
        },
        query: widgetsCommon.query,
      })
      this.dataLayerPush({
        user: userData,
        event: GTM_CONSTANTS.LOG_EVENT,
        eventType: AMPLITUDE.SERVER_ERROR,
        payload: err.response,
      })
      Raven.captureException(err)
    }
  },
  async automatedTicketByFlowActions(
    { commit, getters, rootGetters, dispatch },
    additionalTags = null
  ) {
    const { computedTicket, widgetsCommon, flowToExecute, userData } = getters

    const zendeskEnv = rootGetters['widgets/userEnv'] === 'DEV' ? 'DEV' : 'CO'
    const logged = '/api/ticket-manager/tickets'
    const guest = '/api/ticket-manager/tickets/guest/ticket?wait-automation=false'

    const guestArticle = state.common.isGuestArticle
    const authOwner = guestArticle ? 'application_guest' : 'application_user'
    const url = guestArticle ? guest : logged

    const FF_useNewTicketUrl = // eslint-disable-line
      rootGetters['countryData/getTicketFlowCountryData']

    const baseZendeskOld = `${ENV.rappi.url[zendeskEnv]}/rappi-zendesk/tickets?zendesk=user`

    const baseZendesk = `${
      ENV.rappi.url[rootGetters['widgets/userEnv']]
    }${url}` // eslint-disable-next-line

    const isTrueNewFlowValue =
      FF_useNewTicketUrl && FF_useNewTicketUrl.value === 'true' // eslint-disable-line
    commit('TOGGLE_FETCH', true)
    dispatch('uploadFiles')
      .then(async ({ data: attachments }) => {
        commit('ADD_ZENDESK_ATTACHMENTS', attachments)

        if (additionalTags)
          computedTicket.tags = computedTicket.tags.concat(additionalTags)

        const ticket = Object.assign({}, computedTicket, {
          comment: {
            body: computedTicket.comment.body,
            uploads: getters.zendeskUploadsTokens,
          },
        })
        return (
          axios
            // eslint-disable-next-line
            .post(
              isTrueNewFlowValue === true ? baseZendesk : baseZendeskOld,
              ticket,
              {
                headers: {
                  Authorization: `Bearer ${widgetsCommon.query.token}`,
                  'auth-owner': authOwner,
                },
              }
            )
        )
      })
      .then(async ({ data: ticket }) => {
        this.dataLayerPush({
          user: userData,
          event: GTM_CONSTANTS.LOG_EVENT,
          eventType: AMPLITUDE.TICKET_CREATED,
          payload: {
            ticket: ticket.ticket ? ticket.ticket.id : ticket.ticketId,
            url: ticket.ticket ? ticket.ticket.url : ticket.url,
            solutionType: flowToExecute,
          },
        })
        const body = {
          flow_id: flowToExecute,
          order_id: widgetsCommon.query.order_id,
          ticket_id: ticket.ticket ? ticket.ticket.id : ticket.ticketId,
        }

        if (ticket._id) {
          await dispatch('widgets/commitQuery', { tim_id: ticket._id }, { root: true })
        }

        const data = Object.assign(
          {},
          {
            body,
          },
          {
            token: widgetsCommon.query.token,
          }
        )
        dispatch('requestFlowAtion', data)
        dispatch('resetZendeskState')
      })
      .catch(async err => {
       await dispatch('resetZendeskState')
        router.push({
          name: 'screen',
          params: {
            screenId: 'ERROR_TICKET_CREATION',
          },
          query: widgetsCommon.query,
        })
        this.dataLayerPush({
          user: userData,
          event: GTM_CONSTANTS.LOG_EVENT,
          eventType: AMPLITUDE.SERVER_ERROR,
          payload: err.response,
        })
        Raven.captureException(err)
      })
  },
  uploadFile({ rootGetters }, file) {
    const zendeskEnv = this.userEnv === 'DEV' ? 'sandbox' : 'prod'

    return axios.post(
      `${ENV.zendesk[zendeskEnv]['baseUrl']}/api/v2/uploads.json?filename=${file.name}`,
      file,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  },
  async uploadFiles({ state, rootGetters, getters }, files) {
    const { widgetsCommon } = getters
    const baseRappi = ENV.rappi.url[rootGetters['widgets/userEnv']]
    let formData = new FormData()

    state.files.forEach(({ imageFile }) => {
      formData.append('attachment', imageFile)
    })

    if (!formData.getAll('attachment').length) {
      return { data: [] };
    }

    return axios.post(
      `${baseRappi}/api/support-attachments/multiple`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${widgetsCommon.query.token}`,
        },
        params: state.ticketQueryParams,
        transformResponse: axios.defaults.transformResponse.concat(({data}) => data)
      }
    )
  },
  requestFlowAtion(
    { state, dispatch, commit, getters, rootGetters },
    { token, body }
  ) {
    const { widgetsCommon, userData } = getters

    const url = `${
      ENV.rappi.url[rootGetters['widgets/userEnv']]
    }/support-actions-manager/flow-actions/execute`

    this.dataLayerPush({
      user: userData,
      event: GTM_CONSTANTS.LOG_EVENT,
      eventType: AMPLITUDE.REQUEST_FLOW,
      payload: body,
    })

    axios
      .post(url, body, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        commit('TOGGLE_FETCH', false)
        router.push({
          name: 'screen',
          params: {
            screenId: data.data.screen_id.toUpperCase(),
          },
          query: widgetsCommon.query,
        })
      })
      .catch(async (err) => {
        commit('TOGGLE_FETCH', false)

        Raven.captureException(err)

        this.dataLayerPush({
          user: userData,
          event: GTM_CONSTANTS.LOG_EVENT,
          eventType: AMPLITUDE.SERVER_ERROR,
          payload: err.response,
        })

        await dispatch('firebaseData/initializeFirebase', null, { root: true })

      })
  },
  async getTicketsByOrder({ commit, state, rootGetters, getters }, orderId) {
    try {
      const { widgetsCommon } = getters
      const baseTim = `${
        ENV.rappi.url[rootGetters['widgets/userEnv']]
      }/api/ticket-manager/tickets/order/${orderId}?relations[0]=tags`
      let { data } = await axios.get(baseTim, {
        headers: {
          Authorization: `Bearer ${widgetsCommon.query.token}`,
        },
      })

      await commit('SET_TICKETS_BY_ORDER', data)
    } catch (err) {
      Raven.captureException(err)
    }
  },
  commitTicketUpdate({ state, dispatch, commit, rootState }, data) {
    commit('SET_TICKET_UPDATE', data)
  },
  commitZendeskCommon({ state, dispatch, commit, rootState }, data) {
    commit('SET_COMMON_UPDATE', data)
  },
  commitRequiredFields({ commit }, data) {
    commit('SET_REQUIRED_FIELDS', data)
  },
  commitCustomField({ state, commit }, data) {
    commit('SET_CUSTOM_FIELD', data)
  },
  addZendeskAttachment({ state, commit }, data) {
    commit('ADD_ZENDESK_ATTACHMENT', data)
  },
  addZendeskAttachments({ state, commit }, data) {
    commit('ADD_ZENDESK_ATTACHMENTS', data)
  },
  removeZendeskAttachment({ state, commit }, data) {
    commit('REMOVE_ZENDESK_ATTACHMENT', data)
  },
  addAttachmentFile({ state, commit }, data) {
    commit('ADD_ATTACHMENT_FILE', data)
  },
  toggleFetching({ state, commit }, data) {
    commit('TOGGLE_FETCH', data)
  },
  summitError({ state, commit }, data) {
    commit('SUMMIT_ERROR', data)
  },
  commitValidatedField({ commit }, data) {
    commit('ADD_VALID_FIELD', data)
  },
  removeValidatedField({ commit }, data) {
    commit('REMOVE_VALID_FIELD', data)
  },
  resetZendeskState({ commit }) {
    commit('RESET_STATE')
  },
  commitTicketsByOrder({ commit }, data) {
    commit('SET_TICKETS_BY_ORDER', data)
  },
  setGuestArticle({ commit }, data) {
    commit('SET_GUEST_ARTICLE', data)
  }
}
